/**
 * Burn markup into a screenshot, and encode the images a reported Issue uploads
 * (browser only — Canvas 2D).
 *
 * The output is ONE raster image with the marks drawn into it, at the screenshot's
 * natural size, so the queue, downloads, Copy for Claude Code and Delyver all see the
 * marks without a special viewer. Colours come from the --markup-* design tokens; text is
 * drawn in the app's body face after the font has loaded.
 */

import {
  arrowHead,
  MARK_COLOR_TOKENS,
  MARK_COLORS,
  type Mark,
  type MarkColor,
  markStyle,
} from "./marks";
import { fitWithin, type Size, shrinkToward } from "./sizing";

export interface MarkPalette {
  pens: Record<MarkColor, string>;
  redact: string;
  halo: string;
  fontFamily: string;
}

function token(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** The live token values (fallbacks only matter if the stylesheet failed to load). */
export function readMarkPalette(): MarkPalette {
  const pens = {} as Record<MarkColor, string>;
  for (const color of MARK_COLORS) pens[color] = token(MARK_COLOR_TOKENS[color]) || "red";
  return {
    pens,
    redact: token("--markup-redact") || "black",
    halo: token("--markup-text-halo") || "white",
    fontFamily: token("--font-sans") || "sans-serif",
  };
}

export function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That image could not be read."));
    };
    img.src = url;
  });
}

function context(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser can't draw images.");
  return ctx;
}

function drawMark(
  ctx: CanvasRenderingContext2D,
  mark: Mark,
  size: Size,
  palette: MarkPalette,
): void {
  const style = markStyle(size.width, size.height);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  switch (mark.kind) {
    case "redact":
      ctx.fillStyle = palette.redact;
      ctx.fillRect(mark.x, mark.y, mark.w, mark.h);
      return;
    case "box":
      ctx.strokeStyle = palette.pens[mark.color];
      ctx.lineWidth = style.strokeWidth;
      ctx.strokeRect(mark.x, mark.y, mark.w, mark.h);
      return;
    case "arrow": {
      const head = arrowHead(
        { x: mark.x1, y: mark.y1 },
        { x: mark.x2, y: mark.y2 },
        style.arrowHeadLength,
      );
      ctx.strokeStyle = palette.pens[mark.color];
      ctx.fillStyle = palette.pens[mark.color];
      ctx.lineWidth = style.strokeWidth;
      ctx.beginPath();
      ctx.moveTo(mark.x1, mark.y1);
      ctx.lineTo(mark.x2, mark.y2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(head.tip.x, head.tip.y);
      ctx.lineTo(head.left.x, head.left.y);
      ctx.lineTo(head.right.x, head.right.y);
      ctx.closePath();
      ctx.fill();
      return;
    }
    case "text": {
      ctx.font = `700 ${style.fontSize}px ${palette.fontFamily}`;
      ctx.textBaseline = "top";
      ctx.lineWidth = Math.max(3, style.fontSize * 0.2);
      ctx.strokeStyle = palette.halo;
      ctx.fillStyle = palette.pens[mark.color];
      mark.text.split("\n").forEach((line, i) => {
        const y = mark.y + i * style.fontSize * 1.25;
        ctx.strokeText(line, mark.x, y);
        ctx.fillText(line, mark.x, y);
      });
      return;
    }
  }
}

/** The screenshot with every mark drawn in, at its natural size. */
export async function renderMarked(
  image: HTMLImageElement,
  marks: readonly Mark[],
): Promise<HTMLCanvasElement> {
  const size = { width: image.naturalWidth, height: image.naturalHeight };
  const palette = readMarkPalette();
  if (marks.some((m) => m.kind === "text")) {
    const style = markStyle(size.width, size.height);
    await document.fonts.load(`700 ${style.fontSize}px ${palette.fontFamily}`).catch(() => []);
  }
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = context(canvas);
  ctx.drawImage(image, 0, 0);
  for (const mark of marks) drawMark(ctx, mark, size, palette);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("The image could not be encoded."))),
      type,
      quality,
    );
  });
}

function resized(source: HTMLCanvasElement, size: Size): HTMLCanvasElement {
  if (size.width === source.width && size.height === source.height) return source;
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = context(canvas);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, size.width, size.height);
  return canvas;
}

/**
 * Encode under `maxBytes`: PNG first (sharp text), then JPEG, then smaller JPEGs. A
 * screenshot that's too big is downscaled to fit rather than refused.
 */
export async function encodeUnder(
  source: HTMLCanvasElement,
  maxBytes: number,
  maxEdge?: number,
): Promise<Blob> {
  let canvas = maxEdge ? resized(source, fitWithin(source, maxEdge)) : source;
  const png = await toBlob(canvas, "image/png");
  if (png.size <= maxBytes) return png;
  let jpeg = await toBlob(canvas, "image/jpeg", 0.88);
  for (let step = 0; jpeg.size > maxBytes && step < 6; step++) {
    canvas = resized(canvas, shrinkToward(canvas, jpeg.size, maxBytes));
    jpeg = await toBlob(canvas, "image/jpeg", 0.85);
  }
  if (jpeg.size > maxBytes) throw new Error("The screenshot is too large to send.");
  return jpeg;
}

export function imageToCanvas(image: HTMLImageElement): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  context(canvas).drawImage(image, 0, 0);
  return canvas;
}
